import type {
  ReactNode,
} from 'react'

import {
  FieldError as AriaFieldError,
  Label as AriaLabel,
  RadioButton,
  RadioField,
  RadioGroup as AriaRadioGroup,
  SelectionIndicator,
  Text,
  type RadioGroupProps as AriaRadioGroupProps,
} from 'react-aria-components'

import type {
  FormFieldErrorMessage,
} from './form-field'

import { cn } from '@/shared/lib/cn'

export interface RadioOption {
  value: string
  label: ReactNode
  description?: ReactNode
  isDisabled?: boolean
}

export interface RadioGroupProps
  extends Omit<
    AriaRadioGroupProps,
    'children' | 'className'
  > {
  label: ReactNode
  options: readonly RadioOption[]

  description?: ReactNode
  errorMessage?: FormFieldErrorMessage

  className?: string
  optionClassName?: string
}

export function RadioGroup({
  label,
  options,
  description,
  errorMessage,
  isRequired = false,
  isInvalid,
  orientation = 'vertical',
  validationBehavior = 'aria',
  className,
  optionClassName,
  ...props
}: RadioGroupProps) {
  const hasStaticError =
    typeof errorMessage === 'string' &&
    errorMessage.trim().length > 0

  return (
    <AriaRadioGroup
      {...props}
      orientation={orientation}
      isRequired={isRequired}
      isInvalid={
        isInvalid ?? hasStaticError
      }
      validationBehavior={validationBehavior}
      className={cn(
        'flex w-full flex-col gap-2',
        className,
      )}
    >
      <AriaLabel className="flex items-center gap-1 text-sm font-medium">
        {label}

        {isRequired ? (
          <span
            aria-hidden="true"
            className="text-(--color-danger)"
          >
            *
          </span>
        ) : null}
      </AriaLabel>

      {description ? (
        <Text
          slot="description"
          className="text-xs leading-5 text-(--color-muted-foreground)"
        >
          {description}
        </Text>
      ) : null}

      <div
        className={cn(
          'flex gap-3',
          orientation === 'horizontal'
            ? 'flex-row flex-wrap'
            : 'flex-col',
        )}
      >
        {options.map((option) => (
          <RadioField
            key={option.value}
            value={option.value}
            isDisabled={option.isDisabled}
            className={cn(
              [
                'rounded-lg border',
                'border-(--color-border)',
                'bg-(--color-surface)',
                'transition-colors',
                'data-selected:border-(--color-primary)',
                'data-selected:bg-(--color-primary-muted)',
                'data-disabled:opacity-50',
              ],
              optionClassName,
            )}
          >
            <RadioButton className="flex w-full cursor-pointer items-start gap-3 rounded-lg p-4 outline-none focus-visible:ring-2 focus-visible:ring-(--color-primary) data-disabled:cursor-not-allowed">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-(--color-border) bg-(--color-surface)">
                <SelectionIndicator className="h-2.5 w-2.5 rounded-full bg-(--color-primary)" />
              </span>

              <span className="min-w-0">
                <span className="block text-sm font-medium">
                  {option.label}
                </span>

                {option.description ? (
                  <Text
                    slot="description"
                    className="mt-1 block text-xs leading-5 text-(--color-muted-foreground)"
                  >
                    {option.description}
                  </Text>
                ) : null}
              </span>
            </RadioButton>
          </RadioField>
        ))}
      </div>

      <AriaFieldError className="text-xs leading-5 text-(--color-danger)">
        {errorMessage}
      </AriaFieldError>
    </AriaRadioGroup>
  )
}
